/** @format */

import { dateFormate, formatDate, formateChange12Hour } from "./DateAndFormat";
import { defaultRules } from "./defaultMessage";
import {
  getAppointmentTimezone,
  isAppointmentUpcoming,
} from "./appointmentStatus";

// Single source of truth lives in utils/imageUtils.js (generic,
// provider-agnostic). Re-exported here so existing imports keep working.
export { compressImageToSquare } from "./imageUtils";

const findDefaultRules = (name, orderId) => {
  const findValue = defaultRules.find(
    (item) => item?.order === orderId && item?.name === name,
  )?.value;
  return findValue;
};

export const rulesFunction = (value, name, orderId) => {
  const findValue = value?.[0]?.[name]?.find(
    (item) => item?.order === orderId,
  )?.value;
  return findValue === undefined ? findDefaultRules(name, orderId) : findValue;
};

// STN-461: "Group appointment slots by provider" (appointment rule 17) is "No". Shared by
// useProviderRender and ProviderFilter so the non-grouped view and the hidden Provider filter agree.
export const isGroupByProviderOff = (rulesConfig) =>
  rulesFunction(rulesConfig, "appointment", 17) === "No";

// Existing patient's last appointment with a provider, and whether the
// scheduler allows showing it (appointment order 23 = prior, 24 = upcoming).
export const getLastAppointmentVisibility = (
  rulesConfig,
  patientdetails,
  providerId,
  allLocations,
  fallbackTimezone,
) => {
  const lastAppointment = patientdetails?.[0]?.lastAppointments?.findLast(
    (item) => item?.providerid === providerId,
  );
  if (!lastAppointment) return { hasLastAppointment: false, isVisible: false };

  const isUpcoming = isAppointmentUpcoming(
    lastAppointment,
    getAppointmentTimezone(lastAppointment, allLocations, fallbackTimezone),
  );
  const ruleValue = rulesFunction(
    rulesConfig,
    "appointment",
    isUpcoming ? 24 : 23,
  );
  return {
    hasLastAppointment: true,
    isVisible: String(ruleValue)?.toLowerCase() === "yes",
  };
};
export const carouselSlider = (value, dataLength) => {
  const slide = value <= dataLength ? value : dataLength || 1;
  return slide;
};

export const dateSkip = (startCount, endCount) => {
  const today = new Date();
  const futureDate = new Date();
  today.setDate(today.getDate() + startCount);
  let count = startCount;
  while (endCount >= count) {
    futureDate.setDate(today.getDate() + count);
    const day = futureDate.getDay();
    count++;
    if (day === 0 || day === 6) endCount++;
  }
  return { start: formatDate(today), end: formatDate(futureDate) };
};
export const businessCalender = (startCount, endCount) => {
  const today = new Date();
  const futureDate = new Date(today); // Copy of today to avoid modifying original

  let workingDays = startCount;
  while (workingDays < endCount) {
    futureDate.setDate(futureDate.getDate() + 1);
    const day = futureDate.getDay();

    if (day !== 0 && day !== 6) {
      workingDays++;
    }
  }
  return { start: today, end: futureDate };
};

export const businessCalenderStartDate = (startCount = 0) => {
  const today = new Date();
  today.setDate(today.getDate() + startCount);

  // Move to next business day if today is a weekend
  while (today.getDay() === 0 || today.getDay() === 6) {
    today.setDate(today.getDate() + 1);
  }

  return today;
};

export const normalCalender = (startCount, endCount) => {
  const currentDate = new Date();
  const futureDate = new Date();
  futureDate.setDate(new Date().getDate() + endCount);
  return { start: currentDate, end: futureDate };
};

export const endDateFunction = (rulesConfig = []) => {
  let dateType = rulesFunction(rulesConfig, "appointment", 2, "BusinessDays");
  let count = parseFloat(rulesFunction(rulesConfig, "appointment", 1, 10));
  if (dateType === "BusinessDays") {
    const { end: date } = businessCalender(0, count);
    return date;
  } else {
    const { end: date } = normalCalender(0, count);
    return date;
  }
};

export const startDateFunction = (rulesConfig = []) => {
  const dateType = rulesFunction(
    rulesConfig,
    "appointment",
    16,
    "BusinessDays",
  );
  const count = parseFloat(rulesFunction(rulesConfig, "appointment", 15, 2));

  if (dateType === "BusinessDays") {
    const { end: date } = businessCalender(0, count);
    if (count !== 0) date.setHours(0, 0, 0, 0);
    return date;
  } else if (dateType === "CalendarDays") {
    const today = new Date();
    today.setDate(today.getDate() + count);
    if (count !== 0) today.setHours(0, 0, 0, 0);
    return today;
  } else {
    const today = new Date();
    today.setHours(today?.getHours() + count);

    return today;
  }
};

/**
 * Check if cookies are allowed in this context
 * @returns {boolean} true if cookies can be set, false if blocked
 */
export function areCookiesAllowed() {
  try {
    // Set test cookie
    document.cookie = "testcookie=1; path=/; SameSite=None; Secure";
    // Check if cookie is present
    const allowed = document.cookie.indexOf("testcookie=") !== -1;
    // Clean up test cookie
    const pastDate = new Date(0);
    document.cookie = `testcookie=; expires=${pastDate.toUTCString()}; path=/; SameSite=None; Secure`;
    return allowed;
  } catch (e) {
    console.error("Cookie check failed", e);
    return false;
  }
}

const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);

export function setCookie(
  key = "ssoToken",
  value,
  path = "/",
  duration = 3600000,
  domain = ".layline.live",
) {
  if (isIphone || !areCookiesAllowed()) {
    return setWithLocalExpiry(key, value, duration);
  } else {
    const date = new Date();
    date.setTime(date.getTime() + duration); // Convert days to milliseconds
    const expires = `expires=${date.toUTCString()}`;
    const domainAttr = `domain=${domain}`;
    document.cookie = `${key}=${value}; ${expires};path=${path}; SameSite=None; Secure`;
  }
}

export function getCookie(name) {
  if (isIphone || !areCookiesAllowed()) {
    return getWithLocalExpiry(name);
  } else {
    const cookies = document?.cookie?.split("; ");
    for (const cookie of cookies) {
      const [key, value] = cookie?.split("=");
      if (key === name) return value;
    }
    return null;
  }
}
export function deleteCookie(key = "ssoToken") {
  if (isIphone || !areCookiesAllowed()) {
    return deleteWithLocalExpiry(key);
  } else {
    const pastDate = new Date(0);
    const expires = `expires=${pastDate.toUTCString()}`;
    const domainAttr = `domain=localhost`;
    document.cookie = `${key}=; ${expires}; ${domainAttr}; path=/; SameSite=None; Secure`;
  }
}

/**
 * Set a value in localStorage with optional expiration time
 * @param {string} key - Storage key
 * @param {any} value - Value to store (string, object, etc.)
 * @param {number} ttl - Time to live in milliseconds (optional)
 */
export function setWithLocalExpiry(key = "accessToken", value, ttl = null) {
  const now = Date.now();
  const item = {
    value,
    expiry: ttl ? now + ttl : null, // null means no expiration
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * Get a value from localStorage, respecting expiration
 * @param {string} key - Storage key
 * @returns {any|null} - Stored value or null if expired/not found
 */
export function getWithLocalExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);

    // Check if expired
    if (item.expiry && Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (e) {
    console.error("Failed to parse storage item", e);
    return null;
  }
}
export function deleteWithLocalExpiry(key) {
  localStorage.removeItem(key);
  const pastDate = new Date(0);
  const expires = `expires=${pastDate.toUTCString()}`;
  const domainAttr = `domain=.layline.live`;
  document.cookie = `${key}=; ${expires}; ${domainAttr}; path=/; SameSite=None; Secure`;
}

export const startCapitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const replaceDateTime = (msg, date, time) => {
  const message = msg
    .replace("APPTDATE", dateFormate(date))
    .replace("APPTTIME", formateChange12Hour(time));
  return message;
};
export const replacePhoneNumber = (msg, number) => {
  const message = msg.replace("PHONENUMBER", number);
  return message;
};

export const removeSearchParams = (name) => {
  const url = new URL(window.location.href);
  if (url.searchParams.has(name)) {
    url.searchParams.delete(name);
    window.history.replaceState({}, "", url.toString());
  }
};
export const replacePath = (name) => {
  const path = window.location.pathname;
  let pagePath = path.split("/");
  let lastPath = pagePath?.[pagePath?.length - 1];
  pagePath = path.replace(lastPath, name);
  return pagePath;
};
export const arrayDuplicateRemover = (array, key) => {
  return Array.from(new Map(array.map((item) => [item?.[key], item])).values());
};

export const lastPath = (loc) => {
  const path = loc || window.location.pathname;
  let pagePath = path.split("/");
  let lastPath = pagePath?.[pagePath?.length - 1];
  return lastPath;
};
export const configText = (keyName, value, replaceValue) => {
  const message = value.replace(keyName, replaceValue);
  return message;
};

export function debounce(fn, delay = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
export const parseIDText = (text) => {
  return {
    name: text.match(/Name[:\s]+([A-Z\s]+)/i)?.[1]?.trim() || "",
    idNumber: text.match(/\b\d{8,14}\b/)?.[0] || "",
    dob: text.match(/\b\d{2}[-/]\d{2}[-/]\d{4}\b/)?.[0] || "",
    gender: text.match(/Male|Female/i)?.[0] || "",
  };
};


export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject(new Error("Failed to convert file to Base64"));
    };
    reader.readAsDataURL(file);
  });
};



export const isTrackingParam = (key) =>
  key?.startsWith("utm_") || key?.startsWith("channel") || key === "gclid";

export const isValidValue = (value) => {
  if (value == null) return false;

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (typeof value === "number" && Number.isNaN(value)) {
    return false;
  }

  return true;
};

export const serviceAllowsAnyAppointmentType = (services = []) =>{
 return services.some(
    (service) =>
      service?.allowAnyAppointmentType === true ||
    Number(service?.allowAnyAppointmentType) === 1,
  );
}

export const isTruthyFlag = (raw) => {
  if (typeof raw === "string") {
    return ["1", "true"].includes(raw.trim().toLowerCase());
  }
  return raw === true || raw === 1;
}

// CT-746 / CT-750 — hard-coded client ID, see CLAUDE.md landmine 11. Kathleen
// asked to remove the location address for TNDI (client 141, all
// schedulers/ruleids) because they only offer the teleconsultation service.
// Only the address (and the map) is hidden; the location name stays visible.
const HIDE_LOCATION_ADDRESS_CLIENT_IDS = [141];

export const shouldHideLocationAddress = (rulesConfig) =>
  HIDE_LOCATION_ADDRESS_CLIENT_IDS.includes(rulesConfig?.[0]?.clientid);

// Blocks the keystroke itself for a "digits only" input, so an invalid
// character never gets drawn into the field at all (onChange rejecting it
// after the fact still lets it flash on screen in some browsers, e.g.
// Firefox, until the next re-render). Navigation/editing keys and any
// Ctrl/Cmd/Alt combo (copy/paste/select-all/etc.) are left alone. Pass as
// the input's onKeyDown; still pair with an onChange guard for paste, which
// doesn't go through per-character keydown events.
const DIGITS_ONLY_NAVIGATION_KEYS = [
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
];

export const digitsOnlyKeyDown = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;

  if (DIGITS_ONLY_NAVIGATION_KEYS.includes(e.key)) return;

  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

// Strips anything non-numeric from the DOM value in place. Pair with
// digitsOnlyKeyDown: that blocks typed keys, but paste (and drag-drop/
// autofill) doesn't fire per-character keydown events and can still land
// non-digit characters in the field — inconsistently across browsers (e.g.
// Firefox lets pasted text straight through; Chrome/Edge still accept a
// bare "e" via paste). Call this first in onChange, then read e.target.value
// — it's already sanitized by the time you do.
export const digitsOnlyChange = (e) => {
  e.target.value = e.target.value.replace(/\D/g, "");
};